import { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ImageOff,
  MessageSquare,
  ThumbsDown,
  ThumbsUp,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import AuthImage from "../components/AuthImage";
import PromptTestPanel from "../components/PromptTestPanel";
import {
  promptTestsApi,
  type PromptTestOptions,
} from "../lib/prompt-tests-api";
import {
  api,
  type FeedbackReason,
  type RatingFilter,
  type RatingsData,
  type UploadsData,
  type Upload,
} from "../lib/api";
import { useConfirm } from "@/components/confirm-dialog";
import { EmptyState } from "@/components/empty-state";
import { ErrorState, StatTile } from "@/components/layout";
import { PageHeader } from "@/components/page-header";
import { RangeSelect, useRangeParam } from "@/components/range-select";
import { StatusBadge } from "@/components/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Field, FieldLabel } from "@/components/ui/field";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUrlParam } from "@/hooks/use-url-state";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

const RATING_FILTERS: { value: RatingFilter; label: string }[] = [
  { value: "all", label: "Alle" },
  { value: "down", label: "👎 Daumen runter" },
  { value: "up", label: "👍 Daumen hoch" },
  { value: "none", label: "Unbewertet" },
];

// Deckt beide Event-Generationen ab: die älteren Namen liegen in der Datenbank
// neben denen, die das Theme heute schickt.
const STEP_LABELS: Record<string, string> = {
  flow_opened: "1. Flow geöffnet",
  personalization_started: "1. Overlay geöffnet",
  photo_upload_started: "1. Foto-Upload gestartet",
  photo_uploaded: "1. Foto hochgeladen",
  consent_given: "2. Einwilligung gegeben",
  preview_requested: "3. Preview angefordert",
  preview_generated: "3. Character sichtbar",
  character_rated: "3. Bewertet",
  preview_rated: "3. Bewertet",
  cover_step_viewed: "4. Cover-Schritt",
  cover_selected: "4. Cover gewählt",
  extras_step_viewed: "5. Extras-Schritt",
  customization_changed: "5. Extra geändert",
  order_review_viewed: "6. Übersicht",
  add_to_cart: "7. In den Warenkorb",
  checkout_start: "8. Checkout gestartet",
  begin_checkout: "8. Checkout gestartet",
  purchase: "9. Gekauft",
};

function stepLabel(u: Upload): string {
  if (!u.furthest_event) return "—";
  return STEP_LABELS[u.furthest_event] ?? u.furthest_event;
}

function pct(part: number, total: number): string {
  return total > 0 ? `${((part / total) * 100).toFixed(0)} %` : "—";
}

function attemptLabel(attempt: number): string {
  return attempt <= 1 ? "Erster Versuch" : `Nach Neu-Generierung (#${attempt})`;
}

/** 👍/👎 am Eintrag. Unbewertet ist ein eigener Zustand, kein Daumen runter. */
function RatingBadge({ upload }: { upload: Upload }) {
  if (!upload.rating)
    return <StatusBadge tone="neutral">unbewertet</StatusBadge>;
  const attempt = upload.rating_attempt ?? 1;
  const suffix = attempt <= 1 ? "1. Versuch" : `Versuch ${attempt}`;
  return upload.rating === "up" ? (
    <StatusBadge tone="success">
      <ThumbsUp className="size-3" aria-label="Daumen hoch" /> {suffix}
    </StatusBadge>
  ) : (
    <StatusBadge tone="critical">
      <ThumbsDown className="size-3" aria-label="Daumen runter" /> {suffix}
    </StatusBadge>
  );
}


// Reihenfolge wie im Theme. `skin` ist ein Alias für `skle`.
const FEEDBACK_REASONS: { id: FeedbackReason; label: string }[] = [
  { id: "face", label: "Gesicht" },
  { id: "hair", label: "Haare" },
  { id: "skle", label: "Hautfarbe" },
  { id: "other", label: "Sonstiges" },
];

function normalizeReason(id: string): string {
  return id === "skin" ? "skle" : id;
}

function feedbackReasonLabel(id: string): string {
  const norm = normalizeReason(id);
  return FEEDBACK_REASONS.find((r) => r.id === norm)?.label ?? id;
}

/** Alle angehakten Gründe eines Uploads, über alle Feedback-Zeilen. */
function uploadReasons(u: Upload): string[] {
  const picked = new Set(
    u.feedback.flatMap((fb) => fb.reasons ?? []).map(normalizeReason),
  );
  const known = FEEDBACK_REASONS.map((r) => r.id).filter((id) => picked.has(id));
  const unknown = [...picked].filter(
    (id) => !FEEDBACK_REASONS.some((r) => r.id === id),
  );
  return [...known, ...unknown];
}

/** Neuester Freitext eines Uploads (feedback ist absteigend sortiert). */
function latestMessage(u: Upload): string | null {
  return u.feedback.find((fb) => fb.message)?.message ?? null;
}

function ReasonPills({ reasons }: { reasons: string[] }) {
  if (reasons.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {reasons.map((r) => (
        <Badge key={r} variant="critical">
          {feedbackReasonLabel(r)}
        </Badge>
      ))}
    </div>
  );
}

export default function Uploads() {
  const confirm = useConfirm();
  const [days, setDays] = useRangeParam(30);
  const [ratingParam, setRating] = useUrlParam("rating", "all");
  const rating = ratingParam as RatingFilter;
  const [reasonParam, setReason] = useUrlParam("reason", "");
  const reason = (reasonParam || null) as FeedbackReason | null;
  const [cropParam, setCropParam] = useUrlParam("crop", "0");
  // Der Kopf-Ausschnitt, der als Image 3 in den Character-Sheet-Request geht,
  // plus dieselbe Preview mit eingezeichnetem Kasten. Standardmäßig aus: er
  // wird pro Bild serverseitig gerechnet und ist nur beim Kalibrieren
  // interessant, nicht bei der täglichen Durchsicht.
  const showCrop = cropParam === "1";
  const [data, setData] = useState<UploadsData | null>(null);
  const [ratings, setRatings] = useState<RatingsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  // Im Query-String, damit sich ein einzelner Upload als Link teilen lässt.
  const [selected, setSelected] = useUrlParam("upload", "");
  // Bücher + Szenen für den Prompt-Test. Einmal für die ganze Seite geladen,
  // damit nicht jeder Upload dieselbe Liste abruft.
  const [testOptions, setTestOptions] = useState<PromptTestOptions | null>(
    null,
  );
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setData(null);
    setError(null);
    api
      .uploads(days, 50, rating, reason)
      .then(setData)
      .catch((e) => setError((e as Error).message));
  }, [days, rating, reason, reloadKey]);

  useEffect(() => {
    api
      .previewRatings(days)
      .then(setRatings)
      .catch(() => setRatings(null));
  }, [days, reloadKey]);

  useEffect(() => {
    promptTestsApi
      .options()
      .then(setTestOptions)
      .catch(() => setTestOptions(null));
  }, []);

  async function handleRemove(uniqid: string, name: string | null) {
    const label = name ? `„${name}“` : uniqid;
    const ok = await confirm({
      title: `Upload ${label} löschen?`,
      description:
        "Die Bilder und alle zugehörigen Events werden aus der Datenbank gelöscht.",
      confirmLabel: "Upload löschen",
      destructive: true,
    });
    if (!ok) return;
    setDeleting(uniqid);
    try {
      await api.deleteUpload(uniqid);
      setData((prev) =>
        prev
          ? {
              ...prev,
              uploads: prev.uploads.filter((x) => x.uniqid !== uniqid),
            }
          : prev,
      );
      setSelected("");
      toast.success(`Upload ${label} gelöscht`);
      setRatings(await api.previewRatings(days));
    } catch (err) {
      toast.error(`Löschen fehlgeschlagen: ${(err as Error).message}`);
    } finally {
      setDeleting(null);
    }
  }

  const current = data?.uploads.find((u) => u.uniqid === selected) ?? null;

  return (
    <div>
      <PageHeader
        title="Uploads"
        meta="Generierte Kunden-Previews prüfen und Prompts daran testen."
        actions={<RangeSelect value={days} onChange={setDays} />}
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <StatTile
          label="👍 Sieht aus wie mein Kind"
          loading={!ratings}
          value={ratings?.up ?? "—"}
          hint={
            ratings &&
            `${pct(ratings.up, ratings.rated)} von ${ratings.rated} bewerteten`
          }
        />
        <StatTile
          label="👎 Noch nicht"
          loading={!ratings}
          value={ratings?.down ?? "—"}
          hint={
            ratings &&
            `${pct(ratings.down, ratings.rated)} von ${ratings.rated} bewerteten`
          }
        />
        <StatTile
          label="Ohne Bewertung"
          loading={!ratings}
          value={ratings?.unrated ?? "—"}
          hint={ratings && `von ${ratings.generated} generierten Previews`}
        />
      </div>
      {ratings && ratings.by_attempt.length > 0 && (
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          {ratings.by_attempt.map((a) => (
            <StatTile
              key={a.attempt}
              label={`👍-Quote · ${attemptLabel(a.attempt)}`}
              value={pct(a.up, a.rated)}
              hint={`👍 ${a.up} · 👎 ${a.down} von ${a.rated} bewerteten`}
            />
          ))}
        </div>
      )}

      {ratings && (
        <Card className="mb-4 gap-2 px-3 py-3">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <h2 className="font-semibold">Was passt nicht?</h2>
            <span className="text-xs text-muted-foreground">
              {ratings.with_reasons > 0
                ? `Angehakte Gründe bei ${ratings.with_reasons} Vorschauen · Klick filtert die Liste`
                : "Noch keine Gründe angehakt in diesem Zeitraum"}
            </span>
            {reason && (
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto h-7"
                onClick={() => setReason("")}
              >
                Filter aufheben
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {FEEDBACK_REASONS.map((r) => {
              const count =
                ratings.by_reason.find((b) => b.reason === r.id)?.count ?? 0;
              const active = reason === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setReason(active ? "" : r.id)}
                  className={`rounded-lg border px-3 py-2 text-left transition-colors outline-none hover:bg-accent focus-visible:ring-3 focus-visible:ring-ring/50 ${active ? "border-critical bg-critical text-critical-foreground hover:bg-critical" : ""}`}
                >
                  <div className="text-xs font-medium">{r.label}</div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-xl font-semibold tabular-nums">
                      {count}
                    </span>
                    <span className="text-xs opacity-70">
                      {pct(count, ratings.with_reasons)}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      <Card className="gap-0 py-0">
        <div className="flex flex-wrap items-center gap-3 border-b px-2 py-1.5">
          <Tabs value={rating} onValueChange={(v) => setRating(v)}>
            <TabsList className="h-auto gap-0.5 bg-transparent p-0">
              {RATING_FILTERS.map((f) => (
                <TabsTrigger
                  key={f.value}
                  value={f.value}
                  className="h-7 flex-none rounded-lg px-2.5 font-medium text-muted-foreground hover:bg-accent data-active:bg-[#e3e3e3] data-active:text-foreground data-active:shadow-none"
                >
                  {f.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Field orientation="horizontal" className="ml-auto w-auto pr-2">
            <Switch
              id="show-crop"
              checked={showCrop}
              onCheckedChange={(v) => setCropParam(v ? "1" : "0")}
            />
            <FieldLabel htmlFor="show-crop" className="text-muted-foreground">
              Gesichtsausschnitt zeigen
            </FieldLabel>
          </Field>
        </div>

        <div className="p-3">
          {error ? (
            <ErrorState
              message={error}
              onRetry={() => setReloadKey((k) => k + 1)}
            />
          ) : !data ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="h-56 rounded-lg" />
              ))}
            </div>
          ) : data.uploads.length === 0 ? (
            <EmptyState
              icon={ImageOff}
              title={
                reason
                  ? `Keine Uploads mit „${feedbackReasonLabel(reason)}“ in diesem Zeitraum`
                  : rating === "all"
                    ? "Keine abgeschlossenen Uploads in diesem Zeitraum"
                    : "Keine Uploads mit dieser Bewertung in diesem Zeitraum"
              }
              action={
                (rating !== "all" || reason) && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setRating("all");
                      setReason("");
                    }}
                  >
                    Alle Uploads zeigen
                  </Button>
                )
              }
            />
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data.uploads.map((u) => (
                <li key={u.uniqid}>
                  <button
                    type="button"
                    onClick={() => setSelected(u.uniqid)}
                    className="w-full overflow-hidden rounded-lg border bg-card text-left transition-shadow outline-none hover:shadow-card focus-visible:ring-3 focus-visible:ring-ring/50"
                  >
                    <div
                      className={`grid gap-px bg-border ${showCrop ? "grid-cols-5" : "grid-cols-3"}`}
                    >
                      <UploadImages upload={u} showCrop={showCrop} />
                    </div>
                    <div className="space-y-1 p-2.5">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-semibold">
                          {u.name ?? "—"}
                        </span>
                        <span className="ml-auto">
                          <RatingBadge upload={u} />
                        </span>
                      </div>
                      <ReasonPills reasons={uploadReasons(u)} />
                      {latestMessage(u) && (
                        <p className="line-clamp-2 text-xs italic">
                          „{latestMessage(u)}“
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{stepLabel(u)}</span>
                        {u.feedback.length > 0 && (
                          <span
                            className="flex items-center gap-0.5"
                            title="Feedback"
                          >
                            <MessageSquare className="size-3" aria-hidden />
                            {u.feedback.length}
                          </span>
                        )}
                        <span className="ml-auto shrink-0">
                          {new Date(u.generated_at).toLocaleString("de-DE", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })}
                        </span>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      <Dialog
        open={current !== null}
        onOpenChange={(o) => !o && setSelected("")}
      >
        {current && data && (
          <UploadDetail
            upload={current}
            index={data.uploads.indexOf(current)}
            total={data.uploads.length}
            onNavigate={(i) => {
              const next = data.uploads[i];
              if (next) setSelected(next.uniqid);
            }}
            showCrop={showCrop}
            testOptions={testOptions}
            deleting={deleting === current.uniqid}
            onRemove={() => handleRemove(current.uniqid, current.name)}
          />
        )}
      </Dialog>
    </div>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("de-DE", {
    dateStyle: "medium",
    timeStyle: "medium",
  });
}

/** Abstand zweier Zeitpunkte, grob gerundet: „45 s“, „3 min 10 s“, „2 h 5 min“. */
function formatDuration(fromIso: string, toIso: string): string | null {
  const s = Math.round((Date.parse(toIso) - Date.parse(fromIso)) / 1000);
  if (!Number.isFinite(s) || s < 0) return null;
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return s % 60 ? `${m} min ${s % 60} s` : `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
  return `${Math.floor(h / 24)} Tage`;
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="m-0 min-w-0 break-words">{children}</dd>
    </>
  );
}

/**
 * Großansicht eines Uploads: Kinderfoto und Vorschau nebeneinander, daneben
 * Bewertung, Schritt und Zeiten. ←/→ blättert durch die gefilterte Liste.
 */
function UploadDetail({
  upload: u,
  index,
  total,
  onNavigate,
  showCrop,
  testOptions,
  deleting,
  onRemove,
}: {
  upload: Upload;
  index: number;
  total: number;
  onNavigate: (index: number) => void;
  showCrop: boolean;
  testOptions: PromptTestOptions | null;
  deleting: boolean;
  onRemove: () => void;
}) {
  const reasons = uploadReasons(u);
  const ratedAfter = u.rated_at ? formatDuration(u.generated_at, u.rated_at) : null;

  function handleKey(e: React.KeyboardEvent) {
    const t = e.target as HTMLElement;
    if (t.closest("input, textarea, select, [contenteditable=true]")) return;
    if (e.key === "ArrowLeft" && index > 0) onNavigate(index - 1);
    if (e.key === "ArrowRight" && index < total - 1) onNavigate(index + 1);
  }

  return (
    <DialogContent
      onKeyDown={handleKey}
      className="flex h-[calc(100dvh-2rem)] flex-col gap-0 p-0 sm:max-w-[min(1400px,calc(100%-2rem))]"
    >
      <DialogHeader className="flex-row items-center gap-3 border-b px-4 py-3 pr-12">
        <div className="min-w-0 flex-1">
          <DialogTitle className="flex items-center gap-2 text-base">
            <span className="truncate">{u.name ?? "Ohne Namen"}</span>
            <RatingBadge upload={u} />
          </DialogTitle>
          <DialogDescription>
            Generiert {formatDateTime(u.generated_at)} ·{" "}
            <span className="font-mono">{u.uniqid}</span>
          </DialogDescription>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            size="icon-sm"
            variant="ghost"
            disabled={index <= 0}
            onClick={() => onNavigate(index - 1)}
            aria-label="Vorheriger Upload"
          >
            <ChevronLeft />
          </Button>
          <span className="text-xs text-muted-foreground tabular-nums">
            {index + 1} / {total}
          </span>
          <Button
            size="icon-sm"
            variant="ghost"
            disabled={index >= total - 1}
            onClick={() => onNavigate(index + 1)}
            aria-label="Nächster Upload"
          >
            <ChevronRight />
          </Button>
        </div>
      </DialogHeader>

      <div className="flex-1 overflow-y-auto">
        <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="grid grid-cols-2 gap-3 self-start">
            <figure className="m-0 overflow-hidden rounded-lg border">
              <AuthImage
                uniqid={u.uniqid}
                kind="reference"
                alt="Hochgeladenes Kinderfoto"
                fit="contain"
                className="aspect-[4/5] max-h-[70dvh]"
              />
              <figcaption className="px-2 py-1 text-xs text-muted-foreground">
                Kinderfoto
              </figcaption>
            </figure>
            <figure className="m-0 overflow-hidden rounded-lg border">
              <AuthImage
                uniqid={u.uniqid}
                kind="generated"
                alt="Generierte Vorschau"
                fit="contain"
                className="aspect-[4/5] max-h-[70dvh]"
              />
              <figcaption className="px-2 py-1 text-xs text-muted-foreground">
                Vorschau
              </figcaption>
            </figure>
          </div>

          <aside className="space-y-4">
            <section className="space-y-2 rounded-lg border p-3">
              <h3 className="font-semibold">Bewertung</h3>
              <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5">
                <Fact label="Urteil">
                  {u.rating === "up"
                    ? "👍 Sieht aus wie mein Kind"
                    : u.rating === "down"
                      ? "👎 Noch nicht"
                      : "unbewertet"}
                </Fact>
                {u.rating && (
                  <Fact label="Versuch">
                    {attemptLabel(u.rating_attempt ?? 1)}
                  </Fact>
                )}
                {u.rated_at && (
                  <Fact label="Bewertet">{formatDateTime(u.rated_at)}</Fact>
                )}
                {ratedAfter && <Fact label="Nach">{ratedAfter}</Fact>}
              </dl>
              <ReasonPills reasons={reasons} />
            </section>

            <section className="space-y-2 rounded-lg border p-3">
              <h3 className="font-semibold">Verlauf</h3>
              <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5">
                <Fact label="Weitester Schritt">{stepLabel(u)}</Fact>
                <Fact label="Generiert">{formatDateTime(u.generated_at)}</Fact>
                <Fact label="Feedback">
                  {u.feedback.length > 0 ? `${u.feedback.length}×` : "keins"}
                </Fact>
              </dl>
            </section>

            <section className="space-y-2 rounded-lg border p-3">
              <h3 className="font-semibold">Kind</h3>
              <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5">
                <Fact label="Name">{u.name ?? "—"}</Fact>
                <Fact label="Geschlecht">{u.gender ?? "—"}</Fact>
                <Fact label="Augenfarbe">{u.eye_color ?? "—"}</Fact>
                <Fact label="Haarfarbe">{u.hair_color ?? "—"}</Fact>
              </dl>
            </section>

            <section className="space-y-2 rounded-lg border p-3">
              <h3 className="font-semibold">Technik</h3>
              <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-xs">
                <Fact label="Shop">{u.shop}</Fact>
                <Fact label="Session">
                  <span className="font-mono">{u.session_id ?? "—"}</span>
                </Fact>
              </dl>
            </section>
          </aside>
        </div>

        <div className="space-y-5 px-4 pb-4">
          {u.feedback.length > 0 && (
            <section className="space-y-2">
              <h3 className="font-semibold">Feedback ({u.feedback.length})</h3>
              <ul className="space-y-2">
                {u.feedback.map((fb, i) => (
                  <li key={i} className="rounded-lg bg-muted p-2.5">
                    <ReasonPills
                      reasons={[...new Set((fb.reasons ?? []).map(normalizeReason))]}
                    />
                    {fb.message && (
                      <p className="mt-1 whitespace-pre-wrap">{fb.message}</p>
                    )}
                    <time className="text-xs text-muted-foreground">
                      {new Date(fb.created_at).toLocaleString("de-DE")}
                    </time>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="space-y-2">
            <h3 className="font-semibold">Weitere Bilder</h3>
            <div
              className="grid max-w-3xl grid-cols-3 gap-2"
            >
              <figure className="m-0 overflow-hidden rounded-lg border">
                <BookImage uniqid={u.uniqid} />
                <figcaption className="px-2 py-1 text-xs text-muted-foreground">
                  Buch
                </figcaption>
              </figure>
              {showCrop && (
                <>
                  <figure className="m-0 overflow-hidden rounded-lg border">
                    <AuthImage
                      uniqid={u.uniqid}
                      kind="face-box"
                      alt="Ausschnitt-Kasten auf der Preview"
                      className="aspect-square"
                    />
                    <figcaption className="px-2 py-1 text-xs text-muted-foreground">
                      Ausschnitt-Kasten
                    </figcaption>
                  </figure>
                  <figure className="m-0 overflow-hidden rounded-lg border">
                    <AuthImage
                      uniqid={u.uniqid}
                      kind="face-crop"
                      alt="Gesichtsausschnitt, der als Bild 3 gesendet wird"
                      className="aspect-square"
                    />
                    <figcaption className="px-2 py-1 text-xs text-muted-foreground">
                      Sheet Bild 3
                    </figcaption>
                  </figure>
                </>
              )}
            </div>
          </section>

          <Separator />
          <PromptTestPanel uniqid={u.uniqid} options={testOptions} />
          <Separator />

          <div className="flex justify-end">
            <Button
              variant="ghost"
              className="text-destructive"
              onClick={onRemove}
              disabled={deleting}
            >
              <Trash2 />
              {deleting ? "Lösche…" : "Upload löschen"}
            </Button>
          </div>
        </div>
      </div>
    </DialogContent>
  );
}

function UploadImages({
  upload: u,
  showCrop,
}: {
  upload: Upload;
  showCrop: boolean;
}) {
  const imgs: { key: string; node: React.ReactNode; caption: string }[] = [
    {
      key: "reference",
      caption: "Hochgeladen",
      node: (
        <AuthImage
          uniqid={u.uniqid}
          kind="reference"
          alt="Hochgeladenes Foto"
          className="aspect-square"
        />
      ),
    },
    {
      key: "generated",
      caption: "Generiert",
      node: (
        <AuthImage
          uniqid={u.uniqid}
          kind="generated"
          alt="Generierte Preview"
          className="aspect-square"
        />
      ),
    },
    {
      key: "book",
      caption: "Buch",
      node: <BookImage uniqid={u.uniqid} />,
    },
  ];
  if (showCrop) {
    imgs.push(
      {
        key: "face-box",
        caption: "Ausschnitt-Kasten",
        node: (
          <AuthImage
            uniqid={u.uniqid}
            kind="face-box"
            alt="Ausschnitt-Kasten auf der Preview"
            className="aspect-square"
          />
        ),
      },
      {
        key: "face-crop",
        caption: "Sheet Bild 3",
        node: (
          <AuthImage
            uniqid={u.uniqid}
            kind="face-crop"
            alt="Gesichtsausschnitt, der als Bild 3 gesendet wird"
            className="aspect-square"
          />
        ),
      },
    );
  }
  return (
    <>
      {imgs.map((i) => (
        <div key={i.key} title={i.caption}>
          {i.node}
        </div>
      ))}
    </>
  );
}

/** Öffentliches Buch-Preview; fehlt es, gleicher Platzhalter wie bei AuthImage. */
function BookImage({ uniqid }: { uniqid: string }) {
  const [failed, setFailed] = useState(false);
  if (failed)
    return (
      <div className="flex aspect-square w-full items-center justify-center bg-muted text-xs text-muted-foreground">
        fehlt
      </div>
    );
  return (
    <img
      className="aspect-square w-full bg-muted object-cover"
      src={`${API_BASE}/images/${uniqid}_book.png`}
      alt="Buch-Preview"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}
